import { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Button
} from '@mui/material';
import { 
  HomeWork as HomeWorkIcon,
  BookOnline as BookOnlineIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  QrCodeScanner as QrCodeScannerIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';
import { useNavigate } from 'react-router-dom';

// Widget component for dashboard stats
const StatsWidget = ({ icon, title, value, color }) => {
  return (
    <Card elevation={3}>
      <CardContent sx={{ 
        display: 'flex', 
        alignItems: 'center',
        p: { xs: 1.5, sm: 2 }, // Адаптивные отступы
      }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${color}.light`,
            color: `${color}.main`,
            borderRadius: '50%',
            p: { xs: 1.5, sm: 2 },
            mr: { xs: 1.5, sm: 2 },
            minWidth: { xs: '40px', sm: '48px' },
            minHeight: { xs: '40px', sm: '48px' }
          }}
        >
          {icon}
        </Box>
        <Box sx={{ overflow: 'hidden' }}>
          <Typography 
            variant="h4" 
            component="div"
            sx={{ 
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {value}
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ whiteSpace: 'nowrap' }}
          >
            {title}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// Recent bookings component
const RecentBookings = ({ bookings, loading }) => {
  return (
    <Card elevation={3} sx={{ height: '100%' }}>
      <CardHeader title="Последние бронирования" />
      <Divider />
      <CardContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List>
            {bookings.length > 0 ? (
              bookings.map((booking) => (
                <Box key={booking.id}>
                  <ListItem>
                    <ListItemText
                      primary={`${booking.User?.name || 'Пользователь'} - ${booking.Property?.name || 'Объект'}`}
                      secondary={`${new Date(booking.startDate).toLocaleDateString()} - ${new Date(booking.endDate).toLocaleDateString()} • ${booking.status}`}
                    />
                    <Typography variant="body2" color="primary">
                      {booking.totalPrice} ₽
                    </Typography>
                  </ListItem>
                  <Divider />
                </Box>
              ))
            ) : (
              <ListItem>
                <ListItemText primary="Нет бронирований" />
              </ListItem>
            )}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

// Recent users component
const RecentUsers = ({ users, loading }) => {
  return (
    <Card elevation={3} sx={{ height: '100%' }}>
      <CardHeader title="Новые пользователи" />
      <Divider />
      <CardContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List>
            {users.length > 0 ? (
              users.map((user) => (
                <Box key={user.id}>
                  <ListItem>
                    <ListItemText
                      primary={user.name}
                      secondary={`${user.email} • ${user.role}`}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </Typography>
                  </ListItem>
                  <Divider />
                </Box>
              ))
            ) : (
              <ListItem>
                <ListItemText primary="Нет новых пользователей" />
              </ListItem>
            )}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuthStore();
  const [stats, setStats] = useState({
    properties: 0,
    bookings: 0,
    users: 0,
    revenue: 0
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  


  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        // Fetch dashboard statistics
        const statsResponse = await axios.get('/api/dashboard/stats', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setStats(statsResponse.data);
        
        // Fetch recent bookings
        const bookingsResponse = await axios.get('/api/dashboard/recent-bookings', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setRecentBookings(bookingsResponse.data);
        
        // Fetch recent users
        const usersResponse = await axios.get('/api/dashboard/recent-users', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setRecentUsers(usersResponse.data);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);
  
  const handleScanQrCode = () => {
    navigate('/admin/scan-coupon');
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: { xs: 3, sm: 4 } }}>
        <Box>
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom
            sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
          >
            Панель управления
          </Typography>
          
          <Typography 
            variant="body1" 
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            Добро пожаловать, {user?.name}! Вот общая статистика вашей платформы.
          </Typography>
        </Box>
        
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<QrCodeScannerIcon />}
          onClick={handleScanQrCode}
          sx={{
            mt: { xs: 2, sm: 0 },
            py: { xs: 1, sm: 1.5 },
            px: { xs: 2, sm: 3 },
            fontSize: { xs: '0.9rem', sm: '1rem' },
            borderRadius: 2,
            boxShadow: 2,
            whiteSpace: 'nowrap',
            '&:hover': {
              boxShadow: 4,
            }
          }}
        >
          Сканировать QR-код
        </Button>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatsWidget 
                icon={<HomeWorkIcon fontSize="large" />} 
                title="Объекты" 
                value={stats.properties} 
                color="primary" 
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsWidget 
                icon={<BookOnlineIcon fontSize="large" />} 
                title="Бронирования" 
                value={stats.bookings} 
                color="secondary" 
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsWidget 
                icon={<PeopleIcon fontSize="large" />} 
                title="Пользователи" 
                value={stats.users} 
                color="success" 
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsWidget 
                icon={<TrendingUpIcon fontSize="large" />} 
                title="Выручка" 
                value={`${stats.revenue.toLocaleString()} ₽`} 
                color="warning" 
              />
            </Grid>
          </Grid>
          
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
            <Grid item xs={12} md={6}>
              <RecentBookings bookings={recentBookings} loading={loading} />
            </Grid>
            <Grid item xs={12} md={6}>
              <RecentUsers users={recentUsers} loading={loading} />
            </Grid>
          </Grid>
          

        </>
      )}
    </Box>
  );
};

export default Dashboard;
